import PropTypes from 'prop-types';

const DIGIT_COUNT = 6;

class SixDigitCodeInput extends React.Component {
  inputRefs = [];
  codeInputKeys = ['digit-1', 'digit-2', 'digit-3', 'digit-4', 'digit-5', 'digit-6'];

  componentDidMount() {
    this.focusFirstInput();
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.disabled && this.props.disabled)
      return;

    if (prevProps.disabled !== this.props.disabled || prevProps.value !== this.props.value)
      this.focusFirstInput();
  }

  focusFirstInput = () => {
    if (!this.props.autoFocus || this.props.disabled)
      return;

    const firstInput = this.inputRefs[0];
    if (!firstInput || document.activeElement === firstInput || this.inputRefs.includes(document.activeElement))
      return;

    firstInput.focus();
  }

  codeDigits = () => {
    const value = this.props.value || '';
    return Array.from({ length: DIGIT_COUNT }, (_digit, index) => value[index] || '');
  }

  emitChange = (digits) => {
    const code = digits.join('');
    this.props.onChange(code);

    if (code.length === DIGIT_COUNT && digits.every((digit) => digit !== '') && this.props.onComplete)
      this.props.onComplete(code);
  }

  handleCodeChange = (index, value) => {
    const digit = value.replace(/[^0-9]/g, '').slice(-1);
    const newCode = this.codeDigits();
    newCode[index] = digit;

    this.emitChange(newCode);

    if (digit && index < DIGIT_COUNT - 1)
      this.inputRefs[index + 1]?.focus();
  }

  handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !this.codeDigits()[index] && index > 0)
      this.inputRefs[index - 1]?.focus();

    if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      this.inputRefs[index - 1]?.focus();
    }

    if (e.key === 'ArrowRight' && index < DIGIT_COUNT - 1) {
      e.preventDefault();
      this.inputRefs[index + 1]?.focus();
    }
  }

  handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/[^0-9]/g, '').slice(0, DIGIT_COUNT);

    if (!pastedData)
      return;

    const newCode = this.codeDigits();
    for (let i = 0; i < pastedData.length && i < DIGIT_COUNT; i++)
      newCode[i] = pastedData[i];

    this.emitChange(newCode);
    this.inputRefs[Math.min(pastedData.length, DIGIT_COUNT - 1)]?.focus();
  }

  render() {
    const code = this.codeDigits();

    return (
      <div
        className={this.props.className}
        style={{ gap: '8px' }}
        onPaste={this.handlePaste}
      >
        {this.codeInputKeys.map((inputKey, index) => (
          <input
            key={inputKey}
            ref={(el) => (this.inputRefs[index] = el)}
            type="text"
            inputMode="numeric"
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus={this.props.autoFocus && index === 0}
            autoComplete="one-time-code"
            maxLength="1"
            value={code[index]}
            onChange={(e) => this.handleCodeChange(index, e.target.value)}
            onKeyDown={(e) => this.handleKeyDown(index, e)}
            disabled={this.props.disabled}
            aria-label={`Ziffer ${index + 1}`}
            className={this.props.inputClassName}
            style={{
              width: 'clamp(38px, 12vw, 50px)',
              height: 'clamp(48px, 14vw, 60px)',
              fontSize: 'clamp(20px, 6vw, 24px)',
              fontWeight: 'bold',
              borderWidth: '2px'
            }}
          />
        ))}
      </div>
    );
  }
}

SixDigitCodeInput.defaultProps = {
  autoFocus: true,
  className: 'd-flex justify-content-center mb-3',
  disabled: false,
  inputClassName: 'form-control form-control-lg text-center'
};

SixDigitCodeInput.propTypes = {
  autoFocus: PropTypes.bool,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  inputClassName: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onComplete: PropTypes.func,
  value: PropTypes.string
};

export default SixDigitCodeInput;
